/* eslint-disable react/display-name */
import React, { useEffect } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import ReactGA from 'react-ga';
import { TapThemeProvider } from '@tapresearch/ui/components';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from 'react-query/devtools';

import store from '../redux';
import AssetContext from '../components/common/AssetContext';
import { BreadcrumbProvider } from '../components/common/Breadcrumb';
import { loadUser } from '../redux/actions/User';
import axios from 'axios';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

window.global = window;
window.React = global.React = React;

ReactGA.initialize('UA-44840552-1');
const queryClient = new QueryClient();

const INSIGHTS_ROOT_PATH = '/insights_platform';

window.initSentry = (sentryDSN, releaseTimestamp, currentEnv) => {
  console.log('starting sentry');

  const ignoreErrors = [
    /__show__deepen/,
    /Access is denied/,
    /anonymous function: captureException/,
    /Blocked a frame with origin/,
    /console is not defined/,
    /event is not defined/,
    /feedConf/,
    /MyIPhoneApp_ModifyShowModalDialog/,
    /vid_mate_check is not defined/,
    /win\.document\.body/,
    /window\._sharedData\.entry_data/,
    /ztePageScrollModule/,
  ];

  Sentry.init({
    dsn: sentryDSN,
    environment: currentEnv,
    release: releaseTimestamp,
    ignoreErrors: ignoreErrors,
    tracesSampleRate: currentEnv === 'development' ? 1.0 : 0.2,
  });
};

const PlatformType = Object.freeze({
  [0]: 'iOS',
  [1]: 'Android',
  [2]: 'Javascript',
  [3]: 'Iframe',
  [4]: 'API',
});

const RouterApp = ({ children, currentUser, assets, ...props }) => {
  // Load user into redux store
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(loadUser(currentUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Add CSRF token to axios headers
  useEffect(() => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] =
      document.querySelector('[name=csrf-token]').content;
  }, []);

  // Use google analytics to track page location changes
  useEffect(() => {
    const trailingSlash =
      location.pathname.charAt(location.pathname.length - 1) === '/' &&
      !location.search;
    const pathName = trailingSlash
      ? location.pathname.slice(0, -1)
      : location.pathname;
    ReactGA.pageview(INSIGHTS_ROOT_PATH + pathName + location.search);
  }, [location]);

  return (
    <AssetContext.Provider value={{ ...assets, ...props, PlatformType }}>
      <BreadcrumbProvider>{children}</BreadcrumbProvider>
    </AssetContext.Provider>
  );
};

function ErrorFallback() {
  return (
    <>
      <p>An error has ocurred</p>
      <a href="#" onClick={() => window.location.reload()}>
        Click here to reload the page
      </a>
    </>
  );
}

export default function App(props) {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <TapThemeProvider>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <Router basename={INSIGHTS_ROOT_PATH}>
              <RouterApp {...props} />
            </Router>
          </Provider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </TapThemeProvider>
    </Sentry.ErrorBoundary>
  );
}
